import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Info, FileText } from "@phosphor-icons/react";
import { BriefingMarkdownWrapper, CitationPill } from "./AIPaneStyled";

/**
 * Renders LLM-authored briefing markdown with inline citations rewritten
 * as styled brand-blue pills.
 *
 * Three citation flavors flow through this component, each surviving in
 * the FE as the same ``CitationPill`` UI but with different click
 * semantics:
 *
 *   1. **New-shape, BE-rewritten** —
 *      ``[Source: doc_id=42, page=7, snippet="..."]`` is converted by
 *      ``doc_pipeline.briefing.report_markdown::_rewrite_citations_to_links``
 *      into a real markdown link
 *      ``[Source: …](/matter/<matter_id>/D42?page=7&q=<urlencoded snippet>)``.
 *      This path is the **single source of truth** for citation URLs
 *      going forward — clicking dispatches a citation-jump event with
 *      target ``matterId``, ``targetDocumentId``, ``page``, and
 *      ``searchQuery`` so subscribers (TabView, InboxDocumentViewer)
 *      can open the right doc in a new tab and run Apryse search-
 *      highlight.
 *
 *   2. **Legacy filename-shape, BE-rewritten** —
 *      ``[Source: Filename.pdf (Page 3)]`` is rewritten by the BE's
 *      ``build_citation_link_builder`` into either
 *      ``[Source: …](/matter/<id>/D<resolved_doc_id>?page=3)`` (when
 *      the filename was matched against a Document on the matter, in
 *      which case we route exactly like flavor 1 — minus the
 *      ``q=snippet`` highlight, which is acceptable since the LLM
 *      didn't supply one) or ``[Source: …](#cite-source)`` (when the
 *      filename couldn't be resolved). For ``#cite-{kind}`` anchors
 *      we render a non-clickable pill so the visible content stays
 *      consistent with the resolved-link case.
 *
 *   3. **Un-rewritten raw tokens** (legacy fallback) —
 *      ``[Source: Filename.pdf (Page 3)]`` / ``[Evidence: ...]`` /
 *      ``[Graph: ...]`` markdown that came from a BE endpoint that
 *      hasn't adopted ``_rewrite_citations_to_links`` yet (e.g.
 *      ``DocumentBriefingView`` for the inbox briefing flow). We
 *      preserve the original FE-side ``dms-citation:`` sentinel
 *      rewriter so those panels keep working without coordinated
 *      cross-endpoint rollouts. ``onCitationClick`` receives the
 *      parsed body and the existing ``dispatchCitationJump`` flow
 *      uses ``parseCitationPage`` to figure out a page jump (when
 *      the citation is a ``Source`` carrying a page).
 */

// Matches an inline ``[Source: …]`` / ``[Evidence: …]`` / ``[Graph: …]``
// citation token in raw markdown that is **not** already wrapped in
// markdown-link syntax. The negative lookahead ``(?!\()`` is the
// load-bearing piece: when the BE has rewritten a citation into
// ``[Source: foo](/matter/123/D42?page=7&q=…)``, the bracket portion
// ``[Source: foo]`` is followed by ``(`` and we MUST skip it. Without
// the lookahead, this regex used to match the bracket portion of a
// rewritten link and wrap a second sentinel link around it, producing
// corrupt markdown like
// ``[Source: …](dms-citation:…)(/matter/123/D42?page=7&q=…)``
// where the BE-supplied URL dangles as plain text and the new-shape
// navigation is silently lost. (Bugbot finding on PR #106.)
//
// Markdown-link syntax allows whitespace between ``]`` and ``(`` per
// CommonMark §6.6 only when the link uses the *reference* form
// ``[label][ref]``; for the *inline* form the open paren must
// immediately follow the close bracket. The BE's rewriter only emits
// the inline form, so a tight lookahead ``(?!\()`` is exactly right.
// Pre-existing un-rewritten tokens like ``[Source: foo.pdf]`` end at
// EOL or whitespace and stay matched as before.
// The negative lookbehind ``(?<!\\)`` is the second load-bearing piece
// (2026-08-25, the Cummings Brief): when the BE rewriter cannot link a
// doc-id citation it emits the pill as a markdown link whose LABEL is
// the bracket-escaped token — ``[\[Source: unlinked document ab067259 ·
// p.3\]](#cite-source)``. The escaped inner token starts with ``\[`` and
// ends with ``\]]``, so the ``(?!\()`` lookahead alone does NOT skip it
// (the char after the matched ``]`` is another ``]``, not ``(``), and
// the sentinel rewriter wrapped the label AGAIN, rendering raw
// ``](dms-citation:%7B…`` text inside the pill. Escaped brackets only
// ever come from the BE rewriter, so skipping them is always right.
const CITATION_RE = /(?<!\\)\[(Source|Evidence|Graph):\s*([^\]]+)\](?!\()/gi;
const SENTINEL_PREFIX = "dms-citation:";

// Matches the BE-produced new-shape URL:
//   /matter/<matter_id>/D<doc_id>?page=<N>&q=<urlencoded snippet>
// Both ``page`` and ``q`` are optional in the regex so legacy filename
// citations that ``build_citation_link_builder`` resolved (which carry
// only ``?page=N``) still get picked up. We accept either order of
// ``page`` and ``q`` because the BE is the only writer today and
// produces a fixed order, but a one-line FE regression should not
// require a coordinated BE deploy if the order ever changes.
const NEW_SHAPE_HREF_RE =
  /^\/matter\/(?<matterId>[\w-]+)\/D(?<docId>[\w-]+)(?:\?(?<query>[^#]*))?$/;

// Matches the legacy fallback anchor that ``_rewrite_citations_to_links``
// emits when ``build_citation_link_builder`` could not resolve the
// citation's filename (e.g. the LLM cited a humanized version of the
// filename that doesn't appear on the matter). Rendered as a non-
// clickable pill so the page still scans cleanly.
const LEGACY_ANCHOR_HREF_RE = /^#cite-(?<kind>source|evidence|graph)$/i;

const _parseQueryString = (query) => {
  if (typeof query !== "string" || query.length === 0) {
    return { page: null, snippet: null };
  }
  const params = new URLSearchParams(query);
  const pageRaw = params.get("page");
  const pageNum = Number.parseInt(pageRaw ?? "", 10);
  const page = Number.isFinite(pageNum) && pageNum > 0 ? pageNum : null;
  const q = params.get("q");
  const snippet = typeof q === "string" && q.length > 0 ? q : null;
  return { page, snippet };
};

/**
 * Parse a BE-rewritten new-shape href into typed routing data.
 * Returns ``null`` when ``href`` doesn't match the expected shape so
 * callers can fall through to the next parser.
 */
export const parseNewShapeCitationHref = (href) => {
  if (typeof href !== "string" || href.length === 0) return null;
  const match = NEW_SHAPE_HREF_RE.exec(href);
  if (!match || !match.groups) return null;
  const matterId = match.groups.matterId;
  const docId = match.groups.docId;
  if (!matterId || !docId) return null;
  const { page, snippet } = _parseQueryString(match.groups.query);
  return {
    kind: "source",
    shape: "new",
    matterId,
    docId,
    page,
    snippet,
  };
};

/**
 * Parse a BE-rewritten legacy-anchor href into typed routing data.
 * Returns ``null`` when ``href`` is not an anchor of the form
 * ``#cite-{kind}``.
 */
export const parseLegacyAnchorCitationHref = (href) => {
  if (typeof href !== "string" || href.length === 0) return null;
  const match = LEGACY_ANCHOR_HREF_RE.exec(href);
  if (!match || !match.groups) return null;
  return {
    kind: match.groups.kind.toLowerCase(),
    shape: "legacy-anchor",
  };
};

// `encodeURIComponent` deliberately leaves `(` and `)` alone (RFC 3986
// "sub-delims"), but markdown link syntax `[label](href)` *does* care
// about parens. A citation body like `report (draft).pdf` would emit
// the literal `(` inside the href, prematurely closing the link group
// and rendering the rest as plain text. Encode them defensively.
const escapeParensForMarkdownHref = (s) =>
  s.replace(/\(/g, "%28").replace(/\)/g, "%29");

const buildCitationHref = (kind, body) => {
  const payload = escapeParensForMarkdownHref(
    encodeURIComponent(JSON.stringify({ kind: kind.toLowerCase(), body: body.trim() }))
  );
  return `${SENTINEL_PREFIX}${payload}`;
};

const parseCitationHref = (href) => {
  if (!href || !href.startsWith(SENTINEL_PREFIX)) return null;
  try {
    const payload = decodeURIComponent(href.slice(SENTINEL_PREFIX.length));
    const parsed = JSON.parse(payload);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_err) {
    return null;
  }
};

/**
 * Rewrite the raw markdown so each citation match becomes a sentinel link
 * we can intercept in the renderer. We also preserve the original visible
 * text so non-DMS markdown viewers (e.g. the Mail Intake source-mode
 * fallback) still render something legible.
 */
export const rewriteCitationsForRender = (markdown) => {
  if (typeof markdown !== "string" || !markdown) return "";
  return markdown.replace(CITATION_RE, (_match, kind, body) => {
    const href = buildCitationHref(kind, body);
    const label = `${kind}: ${body.trim()}`;
    return `[${label}](${href})`;
  });
};

// Per-pill display labels are intentionally compact (`filename.pdf · p.12`,
// `filename.pdf · p.4-6`) so the citation pill stays inline-readable
// inside dense bullet text. We accept the same two body shapes that
// `parseCitationPage` does:
//
//   1. Parenthetical form  — `<filename> (Page <n>)` / `(Page <n>-<m>)` /
//                            `(<n>)` (bare-page shorthand)
//   2. Comma-separated form — `<filename>, Page <n>`
//
// Range citations preserve the range in the visible label (`p.4-6`)
// because that's information the attorney values when scanning, even
// though `applyPageJump` will only navigate to the start page.
const SOURCE_LABEL_PAREN_RE =
  /^(.*?)\s*\((?:\s*Page\s+)?(\d+)(?:\s*-\s*(\d+))?\s*\)\s*$/i;
const SOURCE_LABEL_COMMA_RE = /^(.*?),\s*Page\s+(\d+)\s*$/i;
const RAW_NEW_SHAPE_LABEL_RE = /\b(?:doc_id|snippet)\s*=/i;
const RAW_DOCUMENT_NAME_RE =
  /\b(?:document_name|document_title)\s*=\s*(?:"([^"]+)"|([^,]+))/i;

const titleCaseCitationKind = (kind) => {
  if (typeof kind !== "string" || !kind.trim()) return "Citation";
  const lower = kind.trim().toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const formatCitationLabel = (parsed, fallbackText) => {
  if (!parsed) return fallbackText;
  // New-shape citations carry typed routing data from the backend. Keep
  // readable BE labels (filename + page), but replace raw payload labels
  // (`Source: doc_id=..., page=..., snippet=...`) with a compact fallback.
  if (parsed.shape === "new") {
    const fallback = String(fallbackText || "").trim();
    if (fallback && !RAW_NEW_SHAPE_LABEL_RE.test(fallback)) {
      return fallback;
    }
    const parts = [titleCaseCitationKind(parsed.kind)];
    const docNameMatch = fallback.match(RAW_DOCUMENT_NAME_RE);
    const docName = docNameMatch ? (docNameMatch[1] || docNameMatch[2] || "").trim() : "";
    if (docName) {
      parts.push(docName);
    } else if (parsed.docId != null) {
      parts.push(`doc ${parsed.docId}`);
    }
    if (parsed.page != null) parts.push(`p.${parsed.page}`);
    return parts.join(" · ");
  }
  // Legacy-anchor flavors still don't carry a target document, so the BE
  // label is the only useful text we have.
  if (parsed.shape === "legacy-anchor") {
    return fallbackText;
  }
  const trimmedBody = (parsed.body || "").trim();
  if (parsed.kind === "source") {
    const parenMatch = trimmedBody.match(SOURCE_LABEL_PAREN_RE);
    if (parenMatch) {
      const filename = parenMatch[1].trim();
      const startPage = parenMatch[2];
      const endPage = parenMatch[3];
      const pageStr = endPage ? `${startPage}-${endPage}` : startPage;
      return `${filename} · p.${pageStr}`;
    }
    const commaMatch = trimmedBody.match(SOURCE_LABEL_COMMA_RE);
    if (commaMatch) {
      return `${commaMatch[1]} · p.${commaMatch[2]}`;
    }
    return trimmedBody;
  }
  if (parsed.kind === "evidence") return trimmedBody;
  if (parsed.kind === "graph") return trimmedBody;
  return fallbackText;
};

// ``dms-doc://<document_id>?page=N&q=<words>`` — document links authored
// directly by the Context Analysis LLM (backend PR #1533; page/quote
// params added by backend PR #1794). The id universe is constrained
// server-side to the matter's document index, so the FE only needs to
// parse and dispatch; the caller decides how to open it
// (ContextAnalysisTab renders a quick-look preview modal, seeked to
// ``page`` with ``q`` highlighted when present). Rows computed before
// the page contract shipped carry no query string — both fields are
// null then and the modal opens at page 1 exactly as before.
const DMS_DOC_HREF_RE = /^dms-doc:\/\/([0-9a-zA-Z-]{1,64})(\?[^\s]*)?$/;

export const parseDmsDocHref = (href) => {
  if (typeof href !== "string") return null;
  const match = href.match(DMS_DOC_HREF_RE);
  if (!match) return null;
  let page = null;
  let q = null;
  if (match[2]) {
    // URLSearchParams handles both %20 and the ``+``-joined words the
    // citation contract asks the LLM to emit (``q=SHINSPLINTS+LEG``).
    const params = new URLSearchParams(match[2].slice(1));
    const rawPage = Number.parseInt(params.get("page") ?? "", 10);
    if (Number.isFinite(rawPage) && rawPage >= 1) page = rawPage;
    const rawQ = (params.get("q") || "").trim();
    if (rawQ.length > 0) q = rawQ;
  }
  return { documentId: match[1], page, q };
};

// react-markdown v10's default transform sanitizes unknown protocols
// to ``""``, which would blank both our custom schemes before the
// ``a`` component ever sees them. Whitelist ours; defer to the default
// (GitHub-style) sanitizer for everything else.
const _urlTransform = (url) => {
  if (
    typeof url === "string" &&
    (url.startsWith("dms-doc://") || url.startsWith("dms-citation:"))
  ) {
    return url;
  }
  return defaultUrlTransform(url);
};

// Resolves an ``<a href>`` against the three citation flavors in
// preference order: new-shape > legacy-anchor > FE sentinel rewrite.
// Returns ``null`` for any href that's not a citation (regular link),
// which triggers the default external-link branch.
const _resolveCitationFromHref = (href) => {
  const newShape = parseNewShapeCitationHref(href);
  if (newShape) return newShape;
  const legacyAnchor = parseLegacyAnchorCitationHref(href);
  if (legacyAnchor) return legacyAnchor;
  const sentinel = parseCitationHref(href);
  if (sentinel) return sentinel;
  return null;
};

// New-shape pills are always clickable IF the caller wired a click
// handler — they carry full routing payload. Legacy-anchor pills are
// never clickable (BE couldn't resolve the citation, there's nowhere
// to jump). FE-sentinel pills are clickable when caller wired a
// handler (preserves prior behavior).
const _isClickable = (parsed, hasHandler) => {
  if (!hasHandler) return false;
  if (!parsed) return false;
  if (parsed.shape === "legacy-anchor") return false;
  return true;
};

const BriefingMarkdown = ({ content, onCitationClick, onDocumentLinkClick }) => {
  // The FE-side sentinel rewrite is now a fallback for un-rewritten
  // markdown (e.g. ``DocumentBriefingView`` and other endpoints that
  // haven't adopted ``_rewrite_citations_to_links`` yet). Markdown
  // that the BE has already rewritten (``AnalysisJobReportSerializer``
  // output) passes through unchanged because ``CITATION_RE`` carries
  // a ``(?!\()`` negative lookahead — see the comment on the regex
  // for why this is load-bearing for new-shape and resolved-legacy
  // links coming from the BE.
  const rewritten = useMemo(() => rewriteCitationsForRender(content), [content]);

  const markdownComponents = useMemo(
    () => ({
      a: ({ node: _node, href, children, ...rest }) => {
        const docLink = parseDmsDocHref(href);
        if (docLink) {
          const label = Array.isArray(children)
            ? children.join("")
            : String(children || "");
          const hasHandler = typeof onDocumentLinkClick === "function";
          const open = (event) => {
            event.preventDefault();
            if (hasHandler) {
              onDocumentLinkClick(docLink.documentId, label, event, {
                page: docLink.page,
                q: docLink.q,
              });
            }
          };
          return (
            <CitationPill
              role={hasHandler ? "button" : undefined}
              tabIndex={hasHandler ? 0 : undefined}
              title={label || "Open document"}
              $clickable={hasHandler}
              onClick={open}
              onKeyDown={
                hasHandler
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        open(event);
                      }
                    }
                  : undefined
              }
              data-doc-link-id={docLink.documentId}
            >
              <FileText size={12} weight="regular" aria-hidden="true" />
              <span>{label || "Document"}</span>
            </CitationPill>
          );
        }
        const parsed = _resolveCitationFromHref(href);
        if (parsed) {
          const fallback = Array.isArray(children) ? children.join("") : String(children || "");
          const label = formatCitationLabel(parsed, fallback);
          const hasHandler = typeof onCitationClick === "function";
          const clickable = _isClickable(parsed, hasHandler);
          const onClick = clickable
            ? (event) => {
                event.preventDefault();
                onCitationClick(parsed, event);
              }
            : undefined;
          // Data attributes vary by shape so tests + dev tools can
          // disambiguate without re-parsing the href.
          const dataAttrs = {};
          if (parsed.shape === "new") {
            dataAttrs["data-citation-kind"] = parsed.kind;
            dataAttrs["data-citation-doc-id"] = String(parsed.docId);
            if (parsed.page != null) {
              dataAttrs["data-citation-page"] = String(parsed.page);
            }
            if (parsed.snippet) {
              // ``data-citation-snippet`` retained for tests; not used
              // by the runtime click handler (the snippet flows through
              // the dispatch payload, not via DOM attrs).
              dataAttrs["data-citation-snippet"] = parsed.snippet;
            }
          } else if (parsed.shape === "legacy-anchor") {
            dataAttrs["data-citation-kind"] = parsed.kind;
            dataAttrs["data-citation-resolved"] = "false";
          } else {
            dataAttrs["data-citation-kind"] = parsed.kind;
            dataAttrs["data-citation-body"] = parsed.body;
          }
          return (
            <CitationPill
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              title={fallback}
              $clickable={clickable}
              onClick={onClick}
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCitationClick(parsed, event);
                      }
                    }
                  : undefined
              }
              {...dataAttrs}
            >
              <Info size={12} weight="regular" aria-hidden="true" />
              <span>{label}</span>
            </CitationPill>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
            {children}
          </a>
        );
      },
    }),
    [onCitationClick, onDocumentLinkClick]
  );

  if (!content) return null;

  return (
    <BriefingMarkdownWrapper className="ai-briefing-markdown">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        urlTransform={_urlTransform}
      >
        {rewritten}
      </Markdown>
    </BriefingMarkdownWrapper>
  );
};

BriefingMarkdown.propTypes = {
  content: PropTypes.string,
  onCitationClick: PropTypes.func,
  onDocumentLinkClick: PropTypes.func,
};

BriefingMarkdown.defaultProps = {
  content: "",
  onCitationClick: undefined,
  onDocumentLinkClick: undefined,
};

export default BriefingMarkdown;
